import React, { useEffect, useState } from "react";
import { FileText, MessageSquareText, BookOpen, CheckCircle2, Star } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PageHero, Section, SectionHeading } from "@/components/blocks";
import BuyButton from "@/components/BuyButton";
import Testimonials from "@/components/Testimonials";
import api from "@/lib/api";

const INTERVIEW_PACK = {
  id: "r10", name: "Interview Mastery Pack", price: 49,
  items: ["70+ interview questions", "Model responses", "STAR framework", "Teaching interview preparation", "Catholic school interview support", "Government school interview support"],
};

export default function CareerSupport() {
  const [services, setServices] = useState([]);
  useEffect(() => { api.get("/services").then((r) => setServices(r.data)).catch(() => {}); }, []);
  const byId = (id) => services.find((s) => s.id === id);

  return (
    <div data-testid="career-support-page">
      <PageHero
        eyebrow="Career Support"
        title="Get hired. Get confident. Get started."
        subtitle="Employability services, personalised interview coaching and premium resources built specifically for teaching roles."
        image="https://images.pexels.com/photos/5940842/pexels-photo-5940842.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      <Section>
        <SectionHeading eyebrow="Employability services" title="Everything you need to land the role" />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {["s2", "s1", "s5", "s3"].map((id) => {
            const s = byId(id);
            if (!s) return null;
            return (
              <Card key={id} className={`p-8 flex flex-col border-2 ${id === "s1" ? "border-teal-500 relative" : "border-slate-200"}`} data-testid={`cs-service-${id}`}>
                {id === "s1" && <Badge className="absolute -top-3 left-8 bg-teal-600 hover:bg-teal-600">Best Value</Badge>}
                <h3 className="font-display text-xl font-bold text-slate-900">{s.name}</h3>
                <div className="my-3 flex items-center gap-3 flex-wrap">
                  <span className="text-3xl font-bold text-slate-900">${s.price}</span>{s.unit && <span className="text-slate-500"> {s.unit}</span>}
                  {s.compare_price && <span className="text-slate-400 line-through mr-2" data-testid={`cs-compare-${id}`}>${s.compare_price}</span>}
                  {s.savings && <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 border border-emerald-200" data-testid="bundle-savings-badge">Save ${s.savings} vs buying separately</Badge>}
                </div>
                <ul className="space-y-2.5 flex-1 mb-6">
                  {s.features.map((f) => <li key={f} className="flex items-start gap-2 text-slate-600 text-sm"><CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> {f}</li>)}
                </ul>
                <BuyButton itemId={s.id} itemName={s.name} price={s.price} className="w-full bg-slate-900 hover:bg-teal-700 rounded-full" testId={`cs-buy-${id}`} />
              </Card>
            );
          })}
        </div>
      </Section>

      <div className="bg-slate-50 border-y border-slate-200">
        <Section>
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 mb-3">Interview Resource Library</Badge>
              <h2 className="font-display text-3xl font-bold text-slate-900">{INTERVIEW_PACK.name}</h2>
              <p className="text-slate-600 mt-3">The complete digital resource to walk into any teaching interview fully prepared — for government and Catholic schools alike.</p>
              <ul className="grid sm:grid-cols-2 gap-2.5 mt-6">
                {INTERVIEW_PACK.items.map((it) => <li key={it} className="flex items-start gap-2 text-slate-700 text-sm"><Star className="w-4 h-4 text-amber-400 fill-amber-400 mt-0.5 shrink-0" /> {it}</li>)}
              </ul>
            </div>
            <Card className="p-8 border-slate-200 bg-white text-center">
              <BookOpen className="w-10 h-10 text-teal-600 mx-auto mb-3" />
              <div className="text-4xl font-bold text-slate-900">${INTERVIEW_PACK.price}</div>
              <p className="text-slate-500 text-sm mb-6">Instant digital download</p>
              <BuyButton itemId={INTERVIEW_PACK.id} itemName={INTERVIEW_PACK.name} price={INTERVIEW_PACK.price} className="w-full bg-emerald-600 hover:bg-emerald-700 rounded-full" testId="cs-buy-interview-pack">
                Add to basket · ${INTERVIEW_PACK.price}
              </BuyButton>
            </Card>
          </div>
        </Section>
      </div>

      <Testimonials
        category="Career Support"
        eyebrow="Success story"
        heading="How it worked for a graduate teacher"
        subtitle="Real feedback from someone who used this support to land a teaching role."
      />
    </div>
  );
}
